import React from 'react';
import { PublicDeck, SLIDE_HEIGHT, SLIDE_WIDTH, SlideView, loadFontCss } from '../../../components/decks/render';

declare global {
    interface Window {
        /** Set once fonts and images have loaded: the PDF renderer waits for it. */
        __DECK_READY?: boolean;
        /** Set (to the HTTP status) when the deck can't be shown, so the PDF renderer can stop waiting. */
        __DECK_ERROR?: number;
    }
}

const IMAGE_TIMEOUT_MS = 20000;
const frame = (): Promise<void> => new Promise((r) => requestAnimationFrame(() => r()));

// #region whenImagesLoad
function whenImagesLoad(root: HTMLElement): Promise<void> {
    const imgs = Array.from(root.querySelectorAll('img'));
    const each = imgs.map((img) => (img.complete ? Promise.resolve() : new Promise<void>((r) => {
        img.addEventListener('load', () => r(), { once: true });
        img.addEventListener('error', () => r(), { once: true });
    })).then(() => (img.decode ? img.decode().catch(() => undefined) : undefined)));
    return Promise.race([Promise.all(each).then(() => undefined), new Promise<void>((r) => window.setTimeout(r, IMAGE_TIMEOUT_MS))]);
}

// #region PrintDeck
/**
 * /d/:token?print=1 — every slide stacked at exactly 1920×1080 CSS px, one per page, no controls and no tracking.
 * Sets window.__DECK_READY = true when fonts and images are in and the auto-fit has settled.
 */
export const PrintDeck: React.FunctionComponent<{ deck: PublicDeck; autoPrint?: boolean }> = ({ deck, autoPrint = false }): React.ReactElement => {
    const ref = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        let live = true;
        window.__DECK_READY = false;
        (async () => {
            await loadFontCss(deck.context.theme.font_css_url);
            await frame();
            const fonts = (document as any).fonts;
            if (fonts?.ready) await fonts.ready;
            if (ref.current) await whenImagesLoad(ref.current);
            // Fit boxes re-measure after fonts land; give them two frames.
            await frame();
            await frame();
            if (live) window.__DECK_READY = true;
            // The viewer's fallback when the server can't make a PDF: the browser's own "Save as PDF".
            if (live && autoPrint) {
                window.onafterprint = () => { if (window.history.length > 1) window.history.back(); };
                window.print();
            }
        })();
        return () => { live = false; };
    }, [deck, autoPrint]);
    const slides = deck.doc.slides.filter((s) => !s.hidden);
    return (
        <div ref={ref} style={{ width: SLIDE_WIDTH, background: deck.context.theme.page }}>
            <style>{`@page { size: ${SLIDE_WIDTH}px ${SLIDE_HEIGHT}px; margin: 0; } html, body { margin: 0 !important; padding: 0 !important; background: #fff; }`}</style>
            {slides.map((slide) => (
                <section key={slide.id} className="wd-print-page">
                    <SlideView slide={slide} context={deck.context} mode="print" />
                </section>
            ))}
        </div>
    );
};
